/**
 * Tutorial Provider
 * Manages tutorial state and provides context for the app
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { TutorialPanelProps } from '@cloudscape-design/components';
import { 
  aiAssistantTutorial, 
  AI_ASSISTANT_TUTORIAL_ID, 
  TUTORIAL_STORAGE_KEY 
} from './aiAssistantTutorial';

interface TutorialState {
  dismissed: boolean;
  completed: string[];
}

interface TutorialContextValue {
  currentTutorial: TutorialPanelProps.Tutorial | null;
  currentStep: number;
  tutorials: TutorialPanelProps.Tutorial[];
  isBannerVisible: boolean;
  startTutorial: (tutorial: TutorialPanelProps.Tutorial) => void;
  exitTutorial: () => void;
  completeTutorial: () => void;
  setStep: (step: number) => void;
  dismissBanner: () => void;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

/** Load state from localStorage */
const loadState = (): TutorialState => {
  try {
    const stored = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load tutorial state from localStorage:', error);
  }
  return { dismissed: false, completed: [] };
};

/** Save state to localStorage */
const saveState = (state: TutorialState): void => {
  try {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save tutorial state to localStorage:', error);
  }
};

interface TutorialProviderProps {
  children: React.ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const [persistedState, setPersistedState] = useState<TutorialState>(loadState);
  const [currentTutorial, setCurrentTutorial] = useState<TutorialPanelProps.Tutorial | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Save state changes to localStorage
  useEffect(() => {
    saveState(persistedState);
  }, [persistedState]);

  // Build tutorials list with completion status
  const tutorials = useMemo((): TutorialPanelProps.Tutorial[] => [
    {
      ...aiAssistantTutorial,
      completed: persistedState.completed.includes(AI_ASSISTANT_TUTORIAL_ID),
    },
  ], [persistedState.completed]);

  const startTutorial = useCallback((tutorial: TutorialPanelProps.Tutorial) => {
    setCurrentTutorial(tutorial);
    setCurrentStep(0);
  }, []);

  const exitTutorial = useCallback(() => {
    setCurrentTutorial(null);
    setCurrentStep(0);
  }, []);

  const completeTutorial = useCallback(() => {
    if (currentTutorial) {
      setPersistedState(prev => {
        const completedSet = new Set(prev.completed);
        completedSet.add(AI_ASSISTANT_TUTORIAL_ID);
        return { ...prev, completed: Array.from(completedSet) };
      });
    }
    setCurrentTutorial(null);
    setCurrentStep(0);
  }, [currentTutorial]);

  const setStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const dismissBanner = useCallback(() => {
    setPersistedState(prev => ({ ...prev, dismissed: true }));
  }, []);

  const isBannerVisible = !persistedState.dismissed && 
    !persistedState.completed.includes(AI_ASSISTANT_TUTORIAL_ID) &&
    !currentTutorial;

  const value = useMemo((): TutorialContextValue => ({
    currentTutorial,
    currentStep,
    tutorials,
    isBannerVisible,
    startTutorial,
    exitTutorial,
    completeTutorial,
    setStep,
    dismissBanner,
  }), [
    currentTutorial,
    currentStep,
    tutorials,
    isBannerVisible,
    startTutorial,
    exitTutorial,
    completeTutorial,
    setStep,
    dismissBanner,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = (): TutorialContextValue => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};
